<template>
  <div
    :class="[
      $style.base,
      {
        [$style.isTouch]: device.touch,
        [$style.isMobile]: device.mobile,
        [$style.showArrows]: showArrows
      }
    ]"
  >
    <div ref="container" :class="$style.container">
      <div ref="content" :class="$style.containerInner">
        <SelectCategoryItem
          v-for="(category, index) in categories"
          :key="index"
          :data="category"
          @scrollTo="scroll($event)"
          @select="emit('select', $event)"
        />
      </div>
    </div>
    <div
      v-if="showArrows"
      :class="[
        $style.arrow,
        $style.arrowLeft,
        { [$style.isDisabled]: arrivedState.left }
      ]"
      @click="slideLeft"
      v-test="'category-prev'"
    >
      <IconArrow :isDisabled="arrivedState.left" direction="left" />
    </div>
    <div
      v-if="showArrows"
      :class="[
        $style.arrow,
        $style.arrowRight,
        { [$style.isDisabled]: arrivedState.right }
      ]"
      @click="slideRight"
      v-test="'category-next'"
    >
      <IconArrow :isDisabled="arrivedState.right" direction="right" />
    </div>
  </div>
</template>

<script setup lang="ts">
import IconArrow from '@/components/IconArrow.vue';
import { useServicesStore } from '@/stores/services';
import { device } from '@/user-context';
import { useElementSize, useScroll } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import SelectCategoryItem from './SelectCategoryItem.vue';

const emit = defineEmits(['select']);
const { categories } = storeToRefs(useServicesStore());

const content = ref(null);
const container = ref(null);
const { width: containerWidth } = useElementSize(container);

const showArrows = ref(false);

setTimeout(() => {
  showArrows.value = content.value?.clientWidth > window.innerWidth;
}, 0);

const { x: scrollX, arrivedState } = useScroll(container);

const slideLeft = () => {
  scroll(scrollX.value - containerWidth.value);
};
const slideRight = () => {
  scroll(scrollX.value + containerWidth.value);
};

const scroll = (left: number) => {
  if (!container.value) {
    return;
  }

  container.value.scrollTo({
    left,
    behavior: 'smooth'
  });
};
</script>

<style lang="scss" module>
$btn-size: $spacing * 2 + 16px; //16px is the width of the arrow

.base {
  position: relative;
  z-index: 1;
  @include box-shadow('large', 'bottom');

  &.showArrows {
    padding-left: $btn-size;
    padding-right: $btn-size;
  }

  &:not(.showArrows):not(.isTouch) {
    padding-left: $spacing - 3px;
    padding-right: $spacing - 3px;
  }

  &.isTouch {
    &:before,
    &:after {
      content: '';
      position: absolute;
      top: 0;
      width: $spacing;
      height: 100%;
      z-index: 1;
      pointer-events: none;
    }

    &:before {
      left: 0;
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }
    &:after {
      right: 0;
      background: linear-gradient(
        to left,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  }

  &:not(.isTouch) {
    overflow: hidden;
  }
}

.container {
  display: flex;
  padding: $spacing 0;

  .base.isMobile:not(.showArrows) & {
    width: 100vw; // Fixes a mobile Safari bug with overflow-x: scroll, which seems to break when the container has no width, or a percentage based width
  }

  .base.isMobile.showArrows & {
    width: calc(
      100vw - #{$btn-size * 2}
    ); // Fixes a mobile Safari bug with overflow-x: scroll, which seems to break when the container has no width, or a percentage based width
  }

  .base:not(.isTouch) & {
    overflow: auto;

    //Hide scrollbars since overflow is auto
    -ms-overflow-style: none; // IE and Edge
    scrollbar-width: none; // Firefox

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .base.isTouch & {
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;
  }
}

.containerInner {
  display: flex;
  position: relative; // Needed for correct offsetLeft of the child elements
}

.arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  height: 100%;
  width: $btn-size;
  background-color: inherit;
  cursor: pointer;

  &.isDisabled {
    pointer-events: none;
  }
}

.arrowLeft {
  left: 0;
}

.arrowRight {
  right: 0;
}
</style>
